import { createLogic } from "redux-logic";
import {
    type loadUserSelected,
    LOAD_USER_SELECTED,
    PUSH_USER_SELECTED,
    pushUserSelected,
    RESOLVE_EXTRA_CONFLICTS,
    SET_COLOUR,
    SET_UPHOLSTERY,
    SET_WHEEL,
    setColour,
    setUpholstery,
    setWheel,
    USER_SELECT_COLOUR,
    USER_SELECT_UPHOLSTERY,
    USER_SELECT_WHEEL,
} from "../../actions/BasketActions";
import CarDataConsolidator from "../../../../../shared-logic/utils/classes/CarDataConsolidator";
import * as SessionStorage from "../../../../../../common-deprecated/SessionStorage";
import { find, findKeyInObject, queryStringToObject } from "../../../../../../common-deprecated/utils";
import type { BuildAndBuyLogicType } from "../../store";
import type {
    ColourType,
    PersonalizationType,
    UpholsteryType,
    WheelType,
} from "../../../../../../common-deprecated/types/CommonTypes";

/**
 * This logic has to happen before the other basket logic because it can alter the items which will be set.
 */
const readUserSelectedLogic = (
    createLogic as BuildAndBuyLogicType<ReturnType<typeof setColour | typeof setUpholstery | typeof setWheel>>
)({
    type: [SET_COLOUR, SET_UPHOLSTERY, SET_WHEEL],

    transform({ getState, action }, next) {
        const { basket, items, transaction } = getState();
        const transactionId = action.transaction as string;

        const findUserSelected = (
            searchItems: (Record<string, unknown> & { id: string })[],
            type: PersonalizationType,
        ) => {
            const userSelected = basket.userSelected.filter((selected) => selected.selectedType === type).pop();
            return userSelected && searchItems.find((item) => item.id === userSelected.id);
        };

        // Do not execute userSelected logic when the current action is part of a resolve transaction.
        // This will prevent conflict resolve actions to be overwritten by user selected logic resulting in conflict loops.
        const isConflictTransaction =
            transactionId &&
            transaction.active[transactionId] &&
            transaction.active[transactionId].action === RESOLVE_EXTRA_CONFLICTS;

        let item;
        if (!action.userSelected && !isConflictTransaction) {
            switch (action.type) {
                case SET_COLOUR:
                    item = findUserSelected(CarDataConsolidator.getColours(items.colours, basket), USER_SELECT_COLOUR);
                    if (item) action.colour = item as ColourType;
                    break;

                case SET_UPHOLSTERY:
                    item = findUserSelected(
                        CarDataConsolidator.getUpholsteries(items.upholsteries, items.equipmentUpholsteries, basket),
                        USER_SELECT_UPHOLSTERY,
                    );
                    if (item) action.upholstery = item as UpholsteryType;
                    break;

                case SET_WHEEL:
                    item = findUserSelected(
                        CarDataConsolidator.getWheels(items.wheels, basket, items.packs),
                        USER_SELECT_WHEEL,
                    );
                    // Do not alter the wheel if the basket already contains it.
                    // This will prevent the same wheel from being added again after include/exclude logic explicitly sets a new wheel.
                    if (item && item.id !== basket.wheel.id) action.wheel = item as WheelType;
                    break;
            }
        }

        next(action);
    },
});

/**
 * This logic has to happen after all the other basket logic because it needs the "final" items which will be set.
 */
const setUserSelectedLogic = (
    createLogic as BuildAndBuyLogicType<ReturnType<typeof setColour | typeof setUpholstery | typeof setWheel>>
)({
    type: [SET_COLOUR, SET_UPHOLSTERY, SET_WHEEL],

    process({ action }, dispatch, done) {
        if (action.userSelected) {
            let id;
            let type;

            switch (action.type) {
                case SET_COLOUR:
                    id = action.colour.id;
                    type = USER_SELECT_COLOUR;
                    break;

                case SET_UPHOLSTERY:
                    id = action.upholstery.id;
                    type = USER_SELECT_UPHOLSTERY;
                    break;

                case SET_WHEEL:
                    id = action.wheel.id;
                    type = USER_SELECT_WHEEL;
                    break;
            }

            dispatch(pushUserSelected(type, id));
        }
        done();
    },
});

const pushUserSelectedLogic = (createLogic as BuildAndBuyLogicType<ReturnType<typeof pushUserSelected>>)({
    type: PUSH_USER_SELECTED,

    process({ getState }, _, done) {
        const { basket } = getState();
        SessionStorage.set("userSelected", JSON.stringify(basket.userSelected));
        done();
    },
});

const loadUserSelectedLogic = (createLogic as BuildAndBuyLogicType<ReturnType<typeof loadUserSelected>>)({
    type: LOAD_USER_SELECTED,

    process({ getState }, dispatch, done) {
        const { basket, items, commonSettings } = getState();

        // Assuming if we don't have a hash we don't have a deeplink url or tycode => load userSelected defaults.
        const queryString = queryStringToObject(window.location.search);

        // Check if the current querystring has an item that could be overwritten by user selected logic.
        // If that is the case do not execute userSelected logic.
        // See minimizeBasket function for details on the querystring params.
        const queryHasItem = !!Object.keys(commonSettings.query).find((key) =>
            [
                "c", // Colour
                "w", // Wheel
                "i", // Wheel containing options
                "u", // Upholstery
                "e", // Extra
                "p", // Pack
            ].includes(key),
        );

        if (!queryHasItem && !findKeyInObject("tycode", queryString) && basket.userSelected.length > 0) {
            // Get most recent userSelected values by filtering userSelected for unique type values.
            const userSelected = basket.userSelected.reduce(
                (
                    acc: {
                        selectedType: PersonalizationType;
                        id: string;
                    }[],
                    userSel,
                ) => {
                    if (!acc.find((select) => select.selectedType === userSel.selectedType)) {
                        acc.push(userSel);
                    }
                    return acc;
                },
                [],
            );

            userSelected.forEach((userSel) => {
                const { selectedType, id } = userSel;
                let item;
                switch (selectedType) {
                    case "colour":
                        item = find(id, items.colours);
                        if (item) dispatch(setColour(item));
                        break;

                    case "wheel":
                        // For aygo's this will trigger a wheelConflict with the userSelected items selected.
                        item = find(id, items.wheels);
                        if (item) dispatch(setWheel(item));
                        break;

                    case "upholstery":
                        item = find(id, items.upholsteries);
                        if (item) dispatch(setUpholstery(item));
                        break;
                }
            });
        }

        done();
    },
});

export const firstUserSelectedLogic = [readUserSelectedLogic, pushUserSelectedLogic, loadUserSelectedLogic];
export const lastUserSelectedLogic = [setUserSelectedLogic];
